import { DEFAULT_LOCALE, type Locale } from '@arriba.digital/contracts';

/**
 * Calendar math for the date picker.
 *
 * The browser's own `<input type="date">` popup is localized by the browser or
 * the operating system, not by the page: a Russian Windows keeps showing a
 * Russian calendar while the interface is Spanish, and no amount of `lang`
 * attributes reliably changes that (only recent Chromium honours them, and the
 * popup language follows the browser UI language elsewhere). The picker
 * therefore owns its grid and asks `Intl` — which follows the locale we pass
 * in — for every month, weekday and day label.
 *
 * Everything here is pure and locale-parameterised, so it can be unit tested
 * without a DOM.
 */

/** Parse `YYYY-MM-DD` as a local calendar day, never as a UTC instant. */
export function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  // Reject rolled-over dates such as `2026-02-31`.
  if (date.getMonth() !== Number(match[2]) - 1 || date.getDate() !== Number(match[3])) {
    return null;
  }
  return date;
}

/** Local `YYYY-MM-DD`, the format the API and the URL filters use. */
export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Midnight local time on the same day. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Day 1 avoids the 31st-of-February roll-over that plagues month arithmetic. */
export function addMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

export function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * JS weekday index (0 = Sunday) that the locale starts its week on: Sunday in
 * `en`, Monday in `es` and `ru`.
 */
export function firstDayOfWeek(locale: Locale = DEFAULT_LOCALE): number {
  try {
    const weekInfo = (
      new Intl.Locale(locale) as unknown as { weekInfo?: { firstDay?: number } }
    ).weekInfo;
    // `Intl` numbers days 1 = Monday … 7 = Sunday; JS numbers them 0 = Sunday.
    if (weekInfo?.firstDay) return weekInfo.firstDay % 7;
  } catch {
    /* Older engines without `weekInfo` fall through to Monday. */
  }
  return 1;
}

/**
 * Six weeks of cells covering `month`, padded with the neighbouring months so
 * the grid keeps a constant height while paging through the year.
 */
export function buildMonthGrid(month: Date, firstDay: number = 1): Date[][] {
  const first = startOfMonth(month);
  const lead = (first.getDay() - firstDay + 7) % 7;
  const start = addDays(first, -lead);

  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  );
}

/** Weekday column headers, ordered from the locale's first day of the week. */
export function weekdayLabels(
  locale: Locale = DEFAULT_LOCALE,
  firstDay: number = firstDayOfWeek(locale),
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // Any known Sunday works; only its weekday name is used.
  const sunday = new Date(2026, 0, 4);
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(addDays(sunday, (firstDay + index) % 7)),
  );
}

/**
 * Month and year header: "January 2026", "enero de 2026", "январь 2026 г.".
 * Romance and Slavic locales return a lowercase month, so only the first
 * character is raised — `text-transform: capitalize` would also hit "de".
 */
export function formatMonthYear(date: Date, locale: Locale = DEFAULT_LOCALE): string {
  const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The value shown on the trigger: `15/01/2026` in Spanish, `01/15/2026` in English. */
export function formatDay(date: Date, locale: Locale = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** Screen-reader label for a cell: "jueves, 15 de enero de 2026". */
export function formatDayLong(date: Date, locale: Locale = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(date);
}
